import axios from 'axios'
import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, UserPlus, Info } from 'lucide-react'
import { parsePhoneNumberFromString, AsYouType, type CountryCode } from 'libphonenumber-js'
import { useAuthStore } from '../../stores/authStore'
import { authApi } from '../../lib/api/auth'
import Recaptcha, { type RecaptchaHandle } from '../../components/Recaptcha'
import './auth.css'

interface SignUpForm {
  name: string
  email: string
  password: string
  confirmPassword: string
  // Honeypot — a field name that reads like an ordinary form field to an
  // automated form-filler. Hidden off-screen (see the input below) so no
  // real user ever sees or fills it; anything non-empty here marks the
  // submission as a bot to the backend, which fakes a success response for
  // it instead of erroring (so the bot doesn't adapt and retry).
  website: string
}

export default function SignUp() {
  const navigate = useNavigate()
  const { logout } = useAuthStore()

  const [form, setForm]                 = useState<SignUpForm>({ name: '', email: '', password: '', confirmPassword: '', website: '' })
  const [phone, setPhone]               = useState('')
  const [phoneCountry, setPhoneCountry] = useState('US')
  const [phoneError, setPhoneError]     = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordTip, setShowPasswordTip] = useState(false)
  const [agreedToTerms, setAgreedToTerms]       = useState(false)
  const [termsError, setTermsError]             = useState('')
  const [agreedToSmsEmail, setAgreedToSmsEmail] = useState(false)
  const [error, setError]               = useState('')
  const [loading, setLoading]           = useState(false)
  const tipRef = useRef<HTMLDivElement>(null)

  // ── reCAPTCHA v2 (config-driven: rendered only when enabled by the backend) ──
  const [captchaEnabled, setCaptchaEnabled] = useState(false)
  const [captchaSiteKey, setCaptchaSiteKey] = useState('')
  const [captchaToken, setCaptchaToken]     = useState<string | null>(null)
  const recaptchaRef = useRef<RecaptchaHandle>(null)

  useEffect(() => {
    let active = true
    authApi
      .captchaConfig()
      .then((cfg) => {
        if (!active) return
        setCaptchaEnabled(cfg.enabled && !!cfg.site_key)
        setCaptchaSiteKey(cfg.site_key)
      })
      .catch(() => {
        // If the config can't be fetched, don't render the widget; the backend
        // still enforces verification when it is enabled.
        if (active) setCaptchaEnabled(false)
      })
    return () => { active = false }
  }, [])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (tipRef.current && !tipRef.current.contains(e.target as Node)) {
        setShowPasswordTip(false)
      }
    }
    if (showPasswordTip) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showPasswordTip])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setPhoneError('')
    setTermsError('')

    if (!phone.trim()) {
      setPhoneError('Phone number is required.')
      return
    }
    const parsedPhone = parsePhoneNumberFromString(phone, phoneCountry as CountryCode)
    if (!parsedPhone || !parsedPhone.isValid()) {
      setPhoneError('Please enter a valid phone number.')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Invalid email format')
      return
    }

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }
    if (!/[A-Z]/.test(form.password)) {
      setError('Password must contain at least one uppercase letter (A-Z)')
      return
    }
    if (!/[a-z]/.test(form.password)) {
      setError('Password must contain at least one lowercase letter (a-z)')
      return
    }
    if (!/[0-9]/.test(form.password)) {
      setError('Password must contain at least one number (0-9)')
      return
    }

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (!agreedToTerms) {
      setTermsError('Please agree to the Terms of Service and Privacy Policy to continue.')
      return
    }

    if (captchaEnabled && !captchaToken) {
      setError('Please complete the CAPTCHA verification to continue.')
      return
    }

    setLoading(true)
    try {
      const e164Phone = parsePhoneNumberFromString(phone, phoneCountry as CountryCode)?.format('E.164') ?? phone
      await authApi.register(form.name, form.email, form.password, 'client', agreedToTerms, agreedToSmsEmail, e164Phone, captchaToken, form.website)
      // Clear any existing session so PublicAuthRoute does not redirect away from /signin
      logout()
      navigate('/signin', { replace: true, state: { registered: true } })
    } catch (err: unknown) {
      // reCAPTCHA tokens are single-use — reset so the user can retry.
      if (captchaEnabled) {
        recaptchaRef.current?.reset()
        setCaptchaToken(null)
      }
      if (axios.isAxiosError(err) && err.response?.data?.message) {
        setError(err.response.data.message)
      } else {
        setError(err instanceof Error ? err.message : 'Registration failed')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-container">

        {/* ── Left panel ── */}
        <div className="auth-left">
          <div className="auth-brand">
            <Link to="/"><img src="/InterviewDressRehearsal_LOGO.png" alt="IDR" className="auth-logo" /></Link>
            <h1>Interview Dress Rehearsal</h1>
            <p>Join thousands of professionals who have improved their interview skills with us.</p>
          </div>

          <div className="auth-features">
            {[
              { icon: 'clock',  label: 'Flexible scheduling' },
              { icon: 'users',  label: 'Experienced interviewers' },
              { icon: 'file',   label: 'Detailed feedback reports' },
            ].map(({ label }) => (
              <div key={label} className="feature-item">
                <div className="feature-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22,4 12,14.01 9,11.01" />
                  </svg>
                </div>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="auth-right">

          {/* Mobile-only brand header — hidden on desktop where left panel shows */}
          <div className="auth-mobile-brand">
            <Link to="/">
              <img src="/InterviewDressRehearsal_LOGO.png" alt="IDR" className="auth-mobile-logo" />
            </Link>
            <div className="auth-mobile-brand-text">
              <span className="auth-mobile-name">Interview Dress Rehearsal</span>
              <span className="auth-mobile-tagline">Practice smart. Interview with confidence.</span>
            </div>
          </div>

          <div className="auth-card">
            <div className="auth-header">
              <h2>Create account</h2>
              <p>Get started with Interview Dress Rehearsal</p>
            </div>

            {error && <div className="auth-error">{error}</div>}

            <form onSubmit={handleSubmit} className="auth-form">
              {/* Honeypot field — invisible to real users, unreachable by keyboard
                  Tab, and hidden from screen readers. Positioned off-screen
                  (rather than display:none/visibility:hidden) because some bots
                  skip those but still fill in absolutely-positioned fields. Any
                  bot that populates it is silently no-op'd server-side. */}
              <div
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', top: 0, width: 1, height: 1, overflow: 'hidden', opacity: 0 }}
              >
                <label htmlFor="company-website">Company Website</label>
                <input
                  type="text"
                  id="company-website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  placeholder="Enter your full name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={phoneCountry}
                    onChange={(e) => {
                      setPhoneCountry(e.target.value)
                      setPhone('')
                      setPhoneError('')
                    }}
                    className="form-input"
                    style={{ width: 130, flexShrink: 0 }}
                  >
                    <option value="US">🇺🇸 +1 US</option>
                    <option value="GB">🇬🇧 +44 UK</option>
                    <option value="CA">🇨🇦 +1 CA</option>
                    <option value="AU">🇦🇺 +61 AU</option>
                    <option value="IN">🇮🇳 +91 India</option>
                    <option value="DE">🇩🇪 +49 DE</option>
                    <option value="FR">🇫🇷 +33 FR</option>
                    <option value="SG">🇸🇬 +65 SG</option>
                    <option value="AE">🇦🇪 +971 UAE</option>
                    <option value="NZ">🇳🇿 +64 NZ</option>
                  </select>
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => {
                      const formatted = new AsYouType(phoneCountry as CountryCode).input(e.target.value)
                      setPhone(formatted)
                      if (phoneError) setPhoneError('')
                    }}
                    style={{ flex: 1 }}
                  />
                </div>
                {phoneError && (
                  <p style={{ color: '#d97706', fontSize: 13, margin: '4px 0 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Info size={13} /> {phoneError}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="Enter your email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <label className="form-label" style={{ margin: 0 }}>Password</label>
                  <div ref={tipRef} style={{ position: 'relative', display: 'inline-flex' }}>
                    <button
                      type="button"
                      aria-label="Password requirements"
                      onClick={() => setShowPasswordTip((v) => !v)}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        color: '#6b7280', display: 'flex', alignItems: 'center',
                        lineHeight: 1,
                      }}
                    >
                      <Info size={15} />
                    </button>
                    {showPasswordTip && (
                      <div style={{
                        position: 'absolute', top: 'calc(100% + 8px)', left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#1e2432', color: '#fff', borderRadius: 10,
                        padding: '12px 16px', width: 240, zIndex: 50,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                        fontSize: 13, lineHeight: 1.6,
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#d4af37' }}>
                          Password must include
                        </div>
                        {[
                          '✓  At least 8 characters',
                          '✓  One uppercase letter (A–Z)',
                          '✓  One lowercase letter (a–z)',
                          '✓  One number (0–9)',
                        ].map((rule) => (
                          <div key={rule} style={{ marginBottom: 4 }}>{rule}</div>
                        ))}
                        {/* arrow */}
                        <div style={{
                          position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)',
                          width: 0, height: 0,
                          borderLeft: '6px solid transparent',
                          borderRight: '6px solid transparent',
                          borderBottom: '6px solid #1e2432',
                        }} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="form-input"
                    placeholder="Min. 8 chars, uppercase, lowercase, number"
                    value={form.password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  className="form-input"
                  placeholder="Confirm your password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => { setAgreedToTerms(e.target.checked); if (e.target.checked) setTermsError('') }}
                  />
                  <span>
                    I agree to the{' '}
                    <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>
                  </span>
                </label>
                {termsError && (
                  <p style={{ color: '#d97706', fontSize: 13, margin: '4px 0 0 24px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Info size={13} /> {termsError}
                  </p>
                )}
                <label className="checkbox-label" style={{ marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={agreedToSmsEmail}
                    onChange={(e) => setAgreedToSmsEmail(e.target.checked)}
                  />
                  <span>I consent to receive SMS notifications about bookings, reminders, and account updates. Message and data rates may apply.</span>
                </label>
              </div>

              {captchaEnabled && captchaSiteKey && (
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Recaptcha
                    ref={recaptchaRef}
                    siteKey={captchaSiteKey}
                    onChange={(token) => {
                      setCaptchaToken(token)
                      if (token) setError('')
                    }}
                    onError={() => setCaptchaToken(null)}
                  />
                  <p style={{ color: '#6b7280', fontSize: 13, margin: '8px 0 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Info size={13} /> Please complete the CAPTCHA verification to continue.
                  </p>
                </div>
              )}

              <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
                {loading ? 'Creating account…' : <><UserPlus size={18} /> Create Account</>}
              </button>
            </form>

            <div className="auth-footer">
              <p>Already have an account? <Link to="/signin">Sign in</Link></p>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
